/**
 * Единый вход «позвонить» для всех кнопок звонка вне экрана звонка: шапка чата, панель друзей,
 * удержание строки списка, «Перезвонить» в истории и в уведомлении о пропущенном.
 *
 * <p><b>Зачем отдельный вход.</b> Каждая кнопка сама делала `navigate('Call', …)`, и у каждой были
 * свои дыры: двойной тап по звонку из панели друзей открывал два экрана звонка (аудит удобства
 * 2026-10-04, P2) — между тапом и переходом там ещё идёт запрос `createDirect`; а когда звонок
 * стало можно свернуть, кнопка в другом чате начала бы второй разговор поверх первого.
 *
 * <p>Модуль лёгкий намеренно: его импортируют список чатов и экран чата, которым нельзя тянуть
 * WebRTC и LiveKit при открытии. Доступность звонков (`voiceCallOk`) вызывающий передаёт сам —
 * у экрана чата она уже посчитана.
 */
import { Platform } from 'react-native';
import { navigationRef } from '../navigation/navigationRef';
import { tr } from '../i18n/runtime';
import { notifyAction } from '../util/confirmDialog';
import { showToast } from '../util/toastBridge';
import {
  getCallSessionMeta,
  isCallSessionLive,
  isSameCallChat,
  type CallSessionParams,
} from './callSessionStore';

/**
 * Сколько после перехода на экран звонка ещё глотаются повторные нажатия. Переход анимируется
 * ~300 мс, и второй тап, пришедший за это время, попадал бы в ту же кнопку под уходящим экраном.
 */
const DOUBLE_TAP_GUARD_MS = 900;

let inFlight = false;

export type StartCallResult =
  /** Открыт экран нового звонка. */
  | 'started'
  /** В этом чате уже идёт разговор — вернули к нему. */
  | 'resumed'
  /** Идёт разговор в другом чате — второй не начинаем, сказали об этом. */
  | 'busy'
  /** Повторное нажатие, пока первое ещё обрабатывается. */
  | 'in_flight'
  /** Звонки на этом устройстве недоступны (Expo Go, браузер без WebRTC). */
  | 'unavailable';

/** Вернуться к идущему звонку, если он есть. */
export function returnToActiveCall(): boolean {
  const meta = getCallSessionMeta();
  if (!meta || !navigationRef.isReady()) return false;
  navigationRef.navigate('Call', meta.params);
  return true;
}

/**
 * Позвонить.
 *
 * @param params параметры экрана звонка или функция, которая их добудет (например, создаст
 *     личный чат с контактом). Ошибка этой функции пробрасывается вызывающему — показывать её
 *     он умеет лучше.
 * @param opts.voiceCallOk звонки доступны на этом устройстве; по умолчанию — да
 */
export async function startDirectCall(
  params: CallSessionParams | (() => Promise<CallSessionParams>),
  opts: { voiceCallOk?: boolean } = {},
): Promise<StartCallResult> {
  if (inFlight) return 'in_flight';
  if (opts.voiceCallOk === false) {
    void notifyAction({
      title: tr('callUnavailableTitle'),
      body: Platform.OS === 'web' ? tr('webrtcWebChat') : tr('webrtcExpoGoChat'),
      okText: tr('dialogOk'),
    });
    return 'unavailable';
  }
  inFlight = true;
  let release = true;
  try {
    const resolved = typeof params === 'function' ? await params() : params;
    const meta = getCallSessionMeta();
    if (meta && isCallSessionLive()) {
      if (isSameCallChat(meta.params, resolved)) {
        returnToActiveCall();
        return 'resumed';
      }
      showToast({
        message: tr('callAlreadyInCall'),
        actionLabel: tr('callReturnAction'),
        onAction: () => {
          returnToActiveCall();
        },
      });
      return 'busy';
    }
    if (!navigationRef.isReady()) return 'unavailable';
    navigationRef.navigate('Call', resolved);
    // Замок держим ещё немного после перехода — см. DOUBLE_TAP_GUARD_MS.
    release = false;
    setTimeout(() => {
      inFlight = false;
    }, DOUBLE_TAP_GUARD_MS);
    return 'started';
  } finally {
    if (release) inFlight = false;
  }
}

/** Только для тестов. */
export function resetStartDirectCallForTests(): void {
  inFlight = false;
}
